import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input, model, output } from '@angular/core';
import { CommonModule } from '@angular/common';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

// Pricing shown on both pages — keep in sync with tidy.com/pricing.
export const BASE_MINIMUM_PER_UNIT = 19;
export const CLEANING_MAINTENANCE_PER_UNIT = 39;
export const WIFI_SENSOR_PER_YEAR = 149;
export const BOX_PER_YEAR = 79;
export const RESERVATION_FEE_PERCENT = 3.9;

/**
 * The "Your Subscription" summary + "Optional Add-Ons" toggles, shared by the subscribe
 * page (`enable-autopilot`, where the toggles pick the plan you're about to buy) and the
 * subscription page (where they change the plan you already have).
 *
 * Presentational only — it owns no plan data. The Cleaning & Maintenance toggle flips
 * optimistically (mat-slide-toggle writes its own ngModel on click, so the switch has
 * already moved by the time the event lands) and reports the requested state; a caller
 * that ends up NOT applying the change writes the old value back through the two-way
 * `cleaningMaintenanceOn` binding, which snaps the switch back. WiFi + Noise Sensor and
 * The Box aren't self-serve on either page, so their toggles are rendered disabled and
 * the tap is reported for the caller to gate.
 */
@Component({
  selector: 'subscription-plan-card',
  templateUrl: './subscription-plan-card.component.html',
  styleUrls: ['./subscription-plan-card.component.scss'],
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubscriptionPlanCardComponent {

  readonly BASE_MINIMUM_PER_UNIT = BASE_MINIMUM_PER_UNIT;
  readonly CLEANING_MAINTENANCE_PER_UNIT = CLEANING_MAINTENANCE_PER_UNIT;
  readonly WIFI_SENSOR_PER_YEAR = WIFI_SENSOR_PER_YEAR;
  readonly BOX_PER_YEAR = BOX_PER_YEAR;

  heading = input('Your Subscription');
  /** Renders a link to the right of the heading when set — the subscription page's "Edit Subscription". */
  editLinkText = input<string | null>(null);
  /** Extra line under the monthly total, e.g. the subscription page's next charge date. */
  footnote = input<string | null>(null);
  unitCount = input(1);
  baseMinimumPerUnit = input(BASE_MINIMUM_PER_UNIT);
  cleaningMaintenancePerUnit = input(CLEANING_MAINTENANCE_PER_UNIT);
  feePercent = input(RESERVATION_FEE_PERCENT);
  cleaningMaintenanceOn = model(true);
  /** Freezes the Cleaning & Maintenance toggle, e.g. once the subscribe page has charged for the current choice. */
  cleaningMaintenanceLocked = input(false);

  editLinkClicked = output<void>();
  cleaningMaintenanceToggled = output<boolean>();
  hardwareAddOnTapped = output<void>();

  baseMinimumDollars = computed(() => this.unitCount() * this.baseMinimumPerUnit());
  cleaningMaintenanceDollars = computed(() => this.unitCount() * this.cleaningMaintenancePerUnit());
  monthlyTotal = computed(() => this.cleaningMaintenanceOn()
    ? `$${this.cleaningMaintenanceDollars()}/mo + ${this.feePercent()}% of bookings`
    : `${this.feePercent()}% of bookings ($${this.baseMinimumDollars()}/mo min)`);

  onCleaningMaintenanceToggle(on: boolean) {
    this.cleaningMaintenanceOn.set(on);
    this.cleaningMaintenanceToggled.emit(on);
  }
}
